import { useState } from 'react';
import { Popover, Spin } from 'antd';
import { useQuery } from 'react-query';
import DOMPurify from 'dompurify';
import { getCompanyDescription } from '@/common/services/common-api';
import { API_QUERIES } from '@/constants';
import styles from './styles.module.scss';

type Props = {
  companyName: string;
  companyId: number | null | undefined;
};

function DescriptionContent({ companyId, enabled }: { companyId: number; enabled: boolean }) {
  const { data, isLoading } = useQuery(
    [API_QUERIES.COMPANY_DESCRIPTION, companyId],
    () => getCompanyDescription(companyId),
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );

  if (!enabled || isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <Spin size="small" />
      </div>
    );
  }

  if (!data?.content) {
    return <span className={styles.noDescription}>No description available</span>;
  }

  return (
    <div
      className={styles.descriptionContent}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.content) }}
    />
  );
}

export function CompanyNameWithDescription({ companyName, companyId }: Props) {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  if (!companyId) {
    return <>{companyName}</>;
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen && !hasOpened) {
      setHasOpened(true);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger={['hover', 'click']}
      title={companyName}
      content={<DescriptionContent companyId={companyId} enabled={hasOpened} />}
      overlayClassName={styles.popoverOverlay}
      mouseEnterDelay={0.4}
    >
      <span className={styles.companyName}>{companyName}</span>
    </Popover>
  );
}
